import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Close } from "@mui/icons-material";
import { Button, Typography } from "@mui/material";
import { Box, SystemStyleObject } from "@mui/system";
import { StyledFileChip, UploadContainer } from "./styles";
import { useRef, useState } from "react";

// The app's actual theme "primary" colour (src/styles/theme.ts) and derived
// shades, used for the "card" variant below instead of an off-brand blue.
const PRIMARY = "#182F53";
const PRIMARY_LIGHT = "#2C4C7C";
const PRIMARY_DARK = "#0F1F38";
const PRIMARY_TINT = "#E7ECF3";

interface UploadProps {
  sx?: SystemStyleObject;
  onChange?: (files: File[]) => void;
  multiple?: boolean;
  accept?: string;
  name?: string;
  // "card" (the default) is the app-wide dropzone look - gradient icon,
  // headline, explicit Browse button, optional hint/sample-file caption.
  // "default" is the older bare look, kept only for anyone who explicitly
  // opts back into it.
  variant?: "default" | "card";
  hint?: string;
  sampleUrl?: string;
  sampleLabel?: string;
}

function FileDrop({
  sx,
  onChange,
  name = "upload",
  multiple = false,
  accept,
  variant = "card",
  hint,
  sampleUrl,
  sampleLabel = "Need the template?",
}: UploadProps) {
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: any) => {
    if (!e.target.files.length) return;
    if (multiple) {
      setFiles([...files, ...e.target.files]);
      onChange && onChange([...files, ...e.target.files]);
    } else {
      setFiles([e.target.files[0]]);
      onChange && onChange([e.target.files[0]]);
    }
  };

  const handleFileRemove = (e: any, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    onChange && onChange(newFiles);
  };

  const handleDragEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer.files.length) return;
    if (multiple) {
      setFiles([...files, ...e.dataTransfer.files]);
      onChange && onChange([...files, ...e.dataTransfer.files]);
    } else {
      setFiles([e.dataTransfer.files[0]]);
      onChange && onChange([e.dataTransfer.files[0]]);
    }
  };

  const fileChips = files.length !== 0 && (
    <Box px={4} display="flex" justifyContent="center" flexWrap="wrap" mt={3} gap={2}>
      {[...files].map((file, index) => (
        <StyledFileChip key={index}>
          <Typography variant="body2">{file.name}</Typography>
          {multiple && (
            <Close onClick={(e) => handleFileRemove(e, index)} sx={{ fontSize: 14, mt: "3px" }} />
          )}
        </StyledFileChip>
      ))}
    </Box>
  );

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      onChange={handleChange}
      name={name}
      multiple={multiple}
      id={name}
      style={{ display: "none" }}
      {...(accept && { accept })}
    />
  );

  if (variant === "card") {
    return (
      <>
        {hiddenInput}
        <UploadContainer
          sx={{
            border: "none",
            borderRadius: "12px",
            bgcolor: PRIMARY_TINT,
            flexDirection: "column",
            py: 5,
            ...sx,
          }}
          onClick={() => inputRef.current?.click()}
          onDrop={(e) => handleDrop(e)}
          onDragOver={(e) => handleDragOver(e)}
          onDragEnter={(e) => handleDragEnter(e)}
          onDragLeave={(e) => handleDragLeave(e)}
        >
          <div>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "14px",
                background: `linear-gradient(145deg, ${PRIMARY_LIGHT}, ${PRIMARY})`,
                display: "grid",
                placeItems: "center",
                mx: "auto",
                boxShadow: "0 8px 16px -4px rgba(24,47,83,0.4), inset 0 1px 1px rgba(255,255,255,0.5)",
              }}
            >
              <FileUploadOutlinedIcon sx={{ color: "#fff", fontSize: 26 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, mt: 2, textAlign: "center" }}>
              Drag and drop your file here
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.5, textAlign: "center" }}>
              or click below to browse from your computer
            </Typography>
            <Box display="flex" justifyContent="center" mt={2.5}>
              <Button
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  bgcolor: PRIMARY,
                  "&:hover": { bgcolor: PRIMARY_DARK },
                }}
              >
                Browse files
              </Button>
            </Box>
            {(hint || sampleUrl) && (
              <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 1.75, textAlign: "center" }}>
                {hint}
                {hint && sampleUrl && " · "}
                {sampleUrl && (
                  <a
                    href={sampleUrl}
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: PRIMARY, fontWeight: 600, textDecoration: "none" }}
                  >
                    {sampleLabel}
                  </a>
                )}
              </Typography>
            )}
            {fileChips}
          </div>
        </UploadContainer>
      </>
    );
  }

  return (
    <>
      {hiddenInput}
      <label htmlFor={name}>
        <UploadContainer
          sx={sx}
          onDrop={(e) => handleDrop(e)}
          onDragOver={(e) => handleDragOver(e)}
          onDragEnter={(e) => handleDragEnter(e)}
          onDragLeave={(e) => handleDragLeave(e)}
        >
          <div>
            <CloudUploadOutlinedIcon color="disabled" fontSize="large" />
            <Typography color="GrayText" sx={{ textAlign: "center" }}>
              Drag and drop or Browse
            </Typography>
            {fileChips}
          </div>
        </UploadContainer>
      </label>
    </>
  );
}

export default FileDrop;
